/**
 * SEO single source of truth: canonical origin, per-route titles/descriptions,
 * and the sitemap built from them.
 *
 * Consumed from two contexts, so keep this module dependency-free:
 *  - the browser: `SeoHead` (src/components/SeoHead.tsx) looks up the route
 *    entry on navigation and rewrites the head tags;
 *  - the build: the sitemap plugin in vite.config.ts emits `sitemap.xml`
 *    from `buildSitemapXml()`, and robots.txt advertises that URL.
 *
 * Adding a public route? Add its entry here and it is titled, described,
 * canonicalised, and sitemapped in one step. Routes without an entry
 * (auth-gated consoles, dev harnesses) fall back to DEFAULT_SEO and stay out
 * of the sitemap.
 */

import { WIDGETS } from '../widgets/catalog.ts';
import { LISTED_BLOG_POSTS, blogPostPath } from './blogPosts.ts';

/** Canonical prod origin. www + http variants 301 here (tests/e2e/redirects.spec.ts). */
export const SITE_ORIGIN = 'https://oculusmind.ai';

export interface PageSeo {
  /** Route path exactly as registered in App.tsx. */
  readonly path: string;
  /** `<title>` / og:title value. */
  readonly title: string;
  /** meta description / og:description value. */
  readonly description: string;
}

export const DEFAULT_SEO: PageSeo = {
  path: '/',
  title: 'OculusMind.AI — AI Agents for Web Research, Stock Analysis & Automation',
  description:
    'AI agents that search the web with cited sources, run AI stock analysis on live market data, research product details, and automate your workflows. Try one free.',
};

/**
 * Descriptions for the widget detail pages, keyed by widget id. A brief absent
 * here uses its catalog description, which is written to be read on its own.
 */
const WIDGET_DESCRIPTIONS: ReadonlyMap<string, string> = new Map([
  [
    'stock-growth-opportunities',
    'An AI agent ranks five publicly traded companies, from any sector, by the growth their own disclosures support over the next three to six months.',
  ],
  [
    'ai-news',
    "An AI agent summarises the week's most important AI news — new models, GitHub repositories, agent tools and regulation — with every source linked.",
  ],
  [
    'extreme-weather',
    'An AI agent summarises the most severe active US National Weather Service alerts each morning.',
  ],
]);

/**
 * The research briefs (design spec §13). Served as static HTML from the
 * widgets bucket under /widgets/*, NOT as SPA routes — they are listed here
 * solely so the sitemap advertises them. Derived from the catalog so adding a
 * widget cannot silently skip the sitemap.
 *
 * No longer unlinked: as of 2026-08-28 the homepage links them twice — a
 * "Briefs" item in `SiteHeader`'s nav and the pill on `GuestDock`. Both are
 * plain <a>, never react-router <Link>, because these paths are not SPA
 * routes and App's catch-all would redirect a router-handled click to "/".
 */
/**
 * PUBLISHED since 2026-08-28. These are in the sitemap.
 *
 * The gate this carried — "advertising URLs that 404 is worse than shipping
 * nothing" — was correct, and its precondition is now met: `OmWidgets*` has
 * been live since 2026-08-16 and every one of these URLs returns 200
 * (verified against prod 2026-08-28: /widgets and the per-widget pages).
 *
 * Recording why it stayed off for twelve days, because the shape recurs: the
 * gate was a COMMENT, not a check. Nothing consumed `WIDGET_SEO_ENTRIES`, so
 * there was no failing test, no lint error, and no signal of any kind once the
 * pages went live — the sitemap simply kept omitting seven pages that existed.
 * A gate whose release condition nobody is watching is indistinguishable from
 * a deletion.
 */
export const WIDGET_SEO_ENTRIES: readonly PageSeo[] = [
  {
    path: '/widgets',
    title: 'Research Briefs — What Our AI Agents Found | OculusMind.AI',
    // No count and no days: the count went stale once already ("Seven" after
    // six of the seven were retired), and the briefs no longer share one
    // schedule. Each brief's own page states its days, from its cron.
    description:
      'Research briefs produced through the week by OculusMind AI agents running live web search, market data, and the open web.',
  },
  ...WIDGETS.map((w) => ({
    path: `/widgets/${w.id}`,
    title: `${w.title} — Research Brief | OculusMind.AI`,
    description: WIDGET_DESCRIPTIONS.get(w.id) ?? w.workflow.description,
  })),
];






/**
 * Blog posts (`/blog/<YYYY-MM-DD-slug>.html`). Like the widget entries above,
 * these are static HTML served from the bucket, NOT SPA routes — they are
 * listed here solely so the sitemap advertises them. Each post carries its own
 * <title>, description, canonical, and OG tags in its own <head>, so
 * `seoForPath` never sees these paths.
 *
 * Derived from `LISTED_BLOG_POSTS`, so an UNLISTED post cannot reach the
 * sitemap by accident — and, just as importantly, a post cannot be published
 * publicly while silently staying out of the sitemap. That second failure is
 * the one that cost twelve days above; here it is a filter on a field the code
 * reads, not a comment someone has to remember.
 */
export const BLOG_SEO_ENTRIES: readonly PageSeo[] = LISTED_BLOG_POSTS.map((post) => ({
  path: blogPostPath(post),
  title: `${post.title} | OculusMind.AI`,
  description: post.dek,
}));

/**
 * Every public page, in sitemap order. All of these render for anonymous
 * visitors (reCAPTCHA guards the expensive actions behind them, so crawling
 * the pages themselves is safe and intended).
 */
export const PAGE_SEO: readonly PageSeo[] = [
  DEFAULT_SEO,
  {
    path: '/guest',
    title: 'Guest Console — Chat with an AI Agent Free | OculusMind.AI',
    description:
      'Chat with an OculusMind AI agent as a guest — live web search, stock quotes, and product research with no account required.',
  },
  {
    path: '/try',
    title: 'Try OculusMind.AI Free — AI Agents that Search, Think & Act',
    description:
      'Put an AI agent to work in seconds: cited web research, AI stock analysis, product details research, documents, and scheduled workflows.',
  },
  {
    path: '/pricing',
    title: 'Pricing & Plans | OculusMind.AI',
    description:
      'OculusMind.AI plans: free ad-supported access and ad-free paid tiers with higher daily limits for web research, stock analysis, and workflow automation.',
  },
  {
    path: '/integrations',
    title: 'Integrations — A2A, MCP, Email & SMS | OculusMind.AI',
    description:
      'Call OculusMind agents from your own apps over A2A, extend them with MCP tools, or work with them by email and SMS.',
  },
  {
    path: '/blog',
    title: 'Blog — Research Written by AI Agents | OculusMind.AI',
    description:
      'Long-form research drafted by OculusMind.AI agents, with every source checked against the original paper before publication.',
  },
  {
    path: '/models',
    title: 'Open Models — Pico V2, an 8B Tool-Calling LLM | OculusMind.AI',
    description:
      'Pico V2, our Apache-2.0 8B tool-calling model: production-eval results against its base, BFCL V4 by category, and downloads on Hugging Face.',
  },
  {
    path: '/auth/signin',
    title: 'Sign In | OculusMind.AI',
    description: 'Sign in to your OculusMind.AI account to work with your AI agents.',
  },
  {
    path: '/auth/signup',
    title: 'Create an Account | OculusMind.AI',
    description:
      'Create a free OculusMind.AI account and get an AI agent that searches the web, analyzes stocks, researches products, and remembers your context.',
  },
  {
    path: '/legal/terms',
    title: 'Terms of Service | OculusMind.AI',
    description: 'The terms that govern your use of OculusMind.AI services.',
  },
  {
    path: '/legal/privacy',
    title: 'Privacy Policy | OculusMind.AI',
    description: 'How OculusMind.AI collects, uses, and protects your information.',
  },
  {
    path: '/legal/ai-consent',
    title: 'AI Consent | OculusMind.AI',
    description: 'How OculusMind.AI agents use AI models and what you consent to when using them.',
  },
  {
    path: '/legal/about',
    title: 'About | OculusMind.AI',
    description:
      'What OculusMind.AI is, who builds it, and our open model research — including Pico V2, our Apache-2.0 tool-calling model on Hugging Face.',
  },
  {
    path: '/legal/contact',
    title: 'Contact | OculusMind.AI',
    description: 'How to reach the OculusMind.AI team.',
  },
  {
    path: '/legal/licensing',
    title: 'Licensing | OculusMind.AI',
    description: 'License terms for OculusMind.AI services and content.',
  },
  {
    path: '/legal/cookie-policy',
    title: 'Cookie Policy | OculusMind.AI',
    description: 'The cookies OculusMind.AI uses and why.',
  },
  {
    path: '/legal/cookie-preferences',
    title: 'Cookie Preferences | OculusMind.AI',
    description: 'Review and change your OculusMind.AI cookie consent choices.',
  },
  // NOT YET: spread `...WIDGET_SEO_ENTRIES` here in the SAME change that makes
  // CloudFront serve /widgets/*. Until the publisher and that behavior are
  // deployed these URLs 404, and advertising seven dead links would make the
  // crawlable-content case WORSE than shipping nothing — the opposite of what
  // these pages exist for. The entries above are complete and tested; only
  // their publication waits.
];

/** Route → SEO entry, falling back to the site default for unlisted routes
 *  (auth-gated consoles, dev harnesses, unknown paths). */
export function seoForPath(pathname: string): PageSeo {
  const normalized =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return PAGE_SEO.find((p) => p.path === normalized) ?? DEFAULT_SEO;
}

/** Sitemap for the public pages above; emitted as /sitemap.xml at build time
 *  and advertised by the prod robots.txt. */
export function buildSitemapXml(): string {
  const urls = [...PAGE_SEO, ...WIDGET_SEO_ENTRIES, ...BLOG_SEO_ENTRIES]
    .map(
      (p) => `  <url>\n    <loc>${SITE_ORIGIN}${p.path === '/' ? '/' : p.path}</loc>\n  </url>`,
    )
    .join('\n');
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls}\n</urlset>\n`;
}
